import React from "react"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { useLocation } from "@reach/router"
import { tags } from "@tryghost/helpers"
import MetaData from "./metaData"
import JsonData from "./jsonData"
import { getSocialLinks } from "../../utils/urls"
import { defaultLanguage, languageCodes } from "../../i18n/config"
import { isEnOnlyPath } from "../../utils/enOnlyPaths"

// Open Graph locale codes (BCP47-ish) per i18n config language.
// br is Brazilian Portuguese → pt_BR; ua is Ukrainian → uk_UA; by → be_BY.
const OG_LOCALE_MAP = {
  en: "en_US",
  es: "es_ES",
  fr: "fr_FR",
  de: "de_DE",
  pt: "pt_PT",
  br: "pt_BR",
  it: "it_IT",
  th: "th_TH",
  ua: "uk_UA",
  by: "be_BY",
  mt: "mt_MT",
}

function SEO({
  title: setTitle,
  description: setDescription,
  ghostData = {},
  pageType,
}) {
  const loadAllSettings = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
          description
          siteUrl
        }
      }

      allGhostSettings {
        edges {
          node {
            ...GhostSettingsFields
          }
        }
      }
    }
  `)

  const { pathname, href } = useLocation()
  const siteSettings = loadAllSettings.site.siteMetadata
  const ghostSettings = loadAllSettings.allGhostSettings.edges[0].node
  const canonical = `${siteSettings.siteUrl}${pathname}`
  //const urlService = pathname === "/service/"
  //const urlBlog = pathname === slugs.blog

  const {
    mainTitle,
    ogTitle,
    twitterTitle,
    mainDescription,
    ogDescription,
    twitterDescription,
    ogImage,
    ogImageWidth,
    ogImageHeight,
    author,
  } = MetaData({
    title: setTitle,
    description: setDescription,
    ghostData,
    pageType,
  })

  const currentUrl = href
  const devUrl = "https://landev.filmustage.com"

  // Detect current page locale from path; en-only pages (blog, legal) lock to en.
  const firstSegment = pathname.split("/").filter(Boolean)[0]
  const pathLocale =
    firstSegment && languageCodes.includes(firstSegment)
      ? firstSegment
      : defaultLanguage
  const isEnOnlyPage = isEnOnlyPath(pathname)
  const currentLocale = isEnOnlyPage ? defaultLanguage : pathLocale
  const ogLocale =
    OG_LOCALE_MAP[currentLocale] || OG_LOCALE_MAP[defaultLanguage]
  const alternateOgLocales = isEnOnlyPage
    ? []
    : languageCodes
        .filter(code => code !== currentLocale && OG_LOCALE_MAP[code])
        .map(code => OG_LOCALE_MAP[code])

  return (
    <>
      <Helmet>
        {currentUrl?.indexOf(devUrl) === 0 && (
          <meta name="robots" content="noindex, nofollow, noarchive" />
        )}
        {/* <html lang> is set in Layout.js per-locale; do NOT duplicate here. */}
        <title>{mainTitle}</title>

        <meta name="description" content={mainDescription} />
        <link rel="canonical" href={canonical} />
        <meta name="HandheldFriendly" content="true" />

        {(pageType === "blogPost" || ghostData.page) && (
          <meta name="generator" content="Ghost 4.3" />
        )}

        <meta property="og:locale" content={ogLocale} />
        {alternateOgLocales.map(loc => (
          <meta key={loc} property="og:locale:alternate" content={loc} />
        ))}
        <meta property="og:site_name" content={ghostSettings.title} />
        <meta
          property="og:type"
          content={
            pageType === "blogPost"
              ? "article"
              : pageType === "blogByAuthor"
                ? "profile"
                : "website"
          }
        />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content={ogImageWidth} />
        <meta property="og:image:height" content={ogImageHeight} />

        <meta name="twitter:title" content={twitterTitle} />
        <meta name="twitter:description" content={twitterDescription} />
        <meta name="twitter:url" content={canonical} />
        <meta name="twitter:site" content={ghostSettings.twitter} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={ogImage} />

        {(pageType === "blogPost" || ghostData.page) && author?.twitter && (
          <meta name="twitter:creator" content={author.twitter} />
        )}

        {(pageType === "blogPost" || ghostData.page) && (
          <meta name="twitter:label1" content="Written by" />
        )}

        {(pageType === "blogPost" || ghostData.page) && (
          <meta
            name="twitter:data1"
            content={author?.name ? author.name : ghostSettings.title}
          />
        )}

        {(pageType === "blogPost" || ghostData.page) &&
          ghostData.tags?.length > 0 && (
            <meta name="twitter:label2" content="Filed under" />
          )}

        {(pageType === "blogPost" || ghostData.page) &&
          ghostData.tags?.length > 0 && (
            <meta
              name="twitter:data2"
              content={tags(ghostData, { visibility: "public" })}
            />
          )}

        {(pageType === "blogPost" || ghostData.page) && (
          <meta
            property="article:published_time"
            content={ghostData.published_at}
          />
        )}

        {(pageType === "blogPost" || ghostData.page) && (
          <meta
            property="article:modified_time"
            content={ghostData.updated_at}
          />
        )}

        {(pageType === "blogPost" || ghostData.page) &&
          ghostSettings.facebook && (
            <meta
              property="article:publisher"
              content={getSocialLinks("facebook", ghostSettings)}
            />
          )}

        {(pageType === "blogPost" || ghostData.page) && author?.facebook && (
          <meta
            property="article:author"
            content={getSocialLinks("facebook", author)}
          />
        )}

        {pageType === "blogPost" &&
          ghostData.tags?.length > 0 &&
          ghostData.tags
            .filter(({ visibility }) => visibility === "public")
            .map(({ name, slug }) => {
              return <meta property="article:tag" content={name} key={slug} />
            })}
      </Helmet>

      <JsonData
        {...{
          title: setTitle,
          description: setDescription,
          ghostData,
          pageType,
        }}
      />
    </>
  )
}

export default SEO
